/**
 * SectionEditor — editor controlado por block type.
 *
 * Recibe `meta` (estructural) + `title` (i18n) + `value` actual + onChange.
 * Devuelve un editor con UI distinta según `meta.type`. Block types
 * editables:
 *   - Texto: rich_text · plain_text · key_value_grid
 *   - Listas: bullet_list · numbered_list
 *   - Tabla: table (genérica) — structured_table tipada en Commit E
 *   - Diagramas: diagram (Mermaid con tabs Source/Preview) — sub-tipos en Commit E
 *   - Otros: code_block · structured (debug)
 *   - Contenido fijo: fixed_heading (no se llena, es contenedor) ·
 *     fixed_block (no se llena, texto declarado en el template)
 *
 * Block types NO editables hoy (image_upload, file_upload, embed_canon →
 * B.7+) caen al placeholder honesto. Si la sección viene con value
 * preexistente de un type no soportado, lo preserva intacto.
 *
 * Patrones compuestos como ADR (Context/Decision/Consequences) o AC
 * (Given/When/Then) NO son tipos atómicos — se construyen con primitivos
 * anidados en un fixed_heading.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Code2, Eye, Plus, ShieldAlert, Sparkles, Trash2 } from 'lucide-react'

import {
  applySectionDraftRunCitations,
  invalidateVersionAiMetadata,
  useVersionGroundings,
} from '../../../api/citations'
import { useAccessToken } from '../../../auth/useAccessToken'
import { useApiErrorMessage } from '../../../hooks/useApiErrorMessage'
import { normalizeTableSchema } from '../../../lib/structuredTableAdapter'
import { isVersionLockError, markVersionLocked } from '../../../lib/versionLock'
import { pushToast } from '../../../stores/toastStore'
import { SectionObservations } from '../../ai/GroundingFindings'
import {
  type AvailableTokenSection,
  SectionDraftDialog,
} from '../../ai/SectionDraftDialog'
import { RichTextEditor } from '../../editor/RichTextEditor'
import { markdownToTipTapDoc } from '../../editor/value'
import {
  MermaidRender,
  parseDiagramSource,
} from './SectionRenderer'
import {
  MERMAID_PLACEHOLDERS,
  isMermaidType,
  isSupportedBlockType,
  type PathSegment,
  type TemplateSectionMeta,
} from './section_types'

// Tipos de sección que el agente sabe rellenar bien hoy. Para los
// tipos schema-aware (`structured_table`), el backend lee las columnas
// del template snapshot y compone el prompt con las keys exactas.
const AI_SUPPORTED_TYPES = new Set([
  'rich_text',
  'plain_text',
  'bullet_list',
  'numbered_list',
  'checklist',
  'key_value_grid',
  'structured_table',
  'code_block',
  'structured',
  // Sprint AI-1.e — Fase 4: Mermaid (11 subtipos con dispatcher único
  // en el backend). Output esperado: JSON `{"code": "..."}` que el
  // parseMermaidPayload extrae como string raw para el DiagramEditor.
  'mermaid_flowchart',
  'mermaid_sequence',
  'mermaid_use_case',
  'mermaid_activity',
  'mermaid_state',
  'mermaid_er',
  'mermaid_class',
  'mermaid_gantt',
  'mermaid_journey',
  'mermaid_mindmap',
  'mermaid_generic',
])

/** Parser robusto del payload de listas que devuelve el agente.
 *
 * Path principal (post Sprint AI-1.d.b fix): el backend declara en el
 * system prompt que el output debe ser un JSON array de strings, y el
 * agente cumple. Hacemos `JSON.parse` y devolvemos el array.
 *
 * Path fallback (si el LLM ignora el formato): split por líneas,
 * remueve markers de bullet/número, descarta líneas vacías, y filtra
 * los anti-patterns observados en producción:
 *   - preámbulos del LLM ("The purpose of this section is to...")
 *   - "Source: ..." inline cuando el agente cita en línea aparte
 *   - cierres de párrafo ("This is a preliminary list...")
 *
 * Reglas de filtro:
 *   - longitud > 4 chars (filtra basura)
 *   - NO empieza con "Source:" o "Fuente:" (citas extraviadas)
 *   - NO empieza con "The purpose" / "El propósito" (preámbulos)
 */
/** Construye el `instance_key` para la cita IA persistida (ADR-0001
 * Tier 1). Convención path-encoded:
 *
 *   - Sección root → `instance_key = section_id`
 *   - Sección dentro de repetibles → cada segment de path con índice
 *     se encoda como `<sid>[<idx>]`, y se concatena con `.<section_id>`
 *
 * Ejemplo: caller en CU3.flujos_alternos[1].diagrama (root path =
 * casos_de_uso[2]) → `casos_de_uso[2].flujos_alternos[1].diagrama`.
 */
function buildInstanceKey(
  sectionId: string,
  currentPath: { section_id: string; index?: number }[],
): string {
  if (currentPath.length === 0) {
    return sectionId
  }
  const segments = currentPath
    .map((seg) =>
      typeof seg.index === 'number' ? `${seg.section_id}[${seg.index}]` : seg.section_id,
    )
    .filter((s) => s !== sectionId)
  return [...segments, sectionId].join('.')
}


/** Dispatcher type-aware del payload de un agente IA al shape que cada
 * tipo de sección espera. Reutilizable desde:
 *
 *   - `SectionEditor.handleAiAccept` (flow original — Generar sección
 *     con IA).
 *   - El route `ProjectOfficialArtifactEditView` para el flow de
 *     "Subsanar con IA" (Doc 21 Fase 7.g) — el modal devuelve la
 *     propuesta y el route la aplica directamente al `content` state.
 *
 * Tipos no soportados o desconocidos: pasa el payload tal cual; el
 * editor se las arregla o el caller decide qué hacer. */
export function convertAiPayloadForSection(
  payload: string,
  type: string,
  opts: { columns?: unknown[]; defaultLanguage?: string } = {},
): unknown {
  if (type === 'rich_text') {
    // Markdown → HTML → ProseMirror JSON. Sin esto, TipTap muestra
    // `**bold**` literal en el editor.
    return markdownToTipTapDoc(payload)
  }
  if (type === 'plain_text') {
    return payload
  }
  if (type === 'bullet_list' || type === 'numbered_list') {
    return parseListPayload(payload)
  }
  if (type === 'checklist') {
    return parseChecklistPayload(payload)
  }
  if (type === 'key_value_grid') {
    return parseKvGridPayload(payload)
  }
  if (type === 'structured_table') {
    const cols = opts.columns ?? []
    return { rows: parseStructuredTablePayload(payload, cols) }
  }
  if (type === 'code_block') {
    return parseCodeBlockPayload(payload, opts.defaultLanguage ?? 'text')
  }
  if (type === 'structured') {
    return parseStructuredPayload(payload)
  }
  if (isMermaidType(type)) {
    return parseMermaidPayload(payload)
  }
  // Fallback: pegar tal cual.
  return payload
}


function parseListPayload(payload: string): string[] {
  // 1. Path principal — usa el helper compartido que tolera basura
  // trailing/leading (preámbulos del LLM, párrafos de cierre, fenced
  // code blocks). Si extrae el array, aplicamos isMetaItem por si
  // colaron meta-items DENTRO del array.
  const parsed = tryParseJsonArray(payload)
  if (parsed) {
    return parsed
      .map((it) => (typeof it === 'string' ? it : JSON.stringify(it)))
      .map((s) => stripWrappingQuotes(s.trim()))
      .filter((s) => s.length > 0 && !isMetaItem(s))
  }

  // 2. Fallback: split por líneas + filtros anti-patterns. Solo
  // aplica cuando el LLM falló completamente al producir JSON
  // válido — raro pero posible si hay caracteres exóticos sin
  // escape.
  return payload
    .trim()
    .split('\n')
    .map((l) => l.replace(/^\s*(?:[-*•]|\d+\.)\s+/, '').trim())
    .map((l) => stripWrappingQuotes(l))
    .filter((l) => l.length > 4)
    .filter((l) => !isMetaItem(l))
}

/** Quita comillas envolventes de un string. Útil para items que
 * vinieron de un JSON array mal parseado donde quedaron con `"foo,"`
 * literal. */
function stripWrappingQuotes(s: string): string {
  let result = s.trim()
  // Quita coma trailing si quedó del formato JSON
  if (result.endsWith(',')) result = result.slice(0, -1).trim()
  // Doble comilla envolvente
  if (
    result.length >= 2 &&
    ((result.startsWith('"') && result.endsWith('"')) ||
      (result.startsWith("'") && result.endsWith("'")))
  ) {
    return result.slice(1, -1)
  }
  return result
}

/** Anti-patrones que el LLM tiende a colar en listas como meta-items
 * pero que NO son entidades reales del listado. Si el filtro principal
 * (system prompt + JSON parse) falla, este es el segundo anillo de
 * defensa que el SectionEditor aplica antes de pegar al onChange. */
const _META_ITEM_PATTERNS: RegExp[] = [
  // Citas extraviadas
  /^source\s*:/i,
  /^fuente\s*:/i,
  /^cita\s*:/i,
  // Preámbulos del LLM
  /^the\s+purpose\s+of/i,
  /^el\s+prop[óo]sito\s+(?:de|del)/i,
  /^this\s+(?:section|list|document)\s+/i,
  /^esta\s+(?:secci[óo]n|lista)/i,
  /^the\s+following\s+/i,
  /^a\s+continuaci[óo]n/i,
  // Notas / criterios meta — observados en producción
  /^selection\s+criteria\b/i,
  /^criterios?\s+de\s+selecci[óo]n\b/i,
  /^methodology\s*:/i,
  /^metodolog[íi]a\s*:/i,
  /^scope(?:\s+and\s+assumptions?)?\s*:/i,
  /^alcance\s+y\s+(?:supuestos?|asunciones?)/i,
  /^dependencies(?:\s+and\s+expected\s+engagement)?\s*:/i,
  /^dependencias\b/i,
  /^notes?\s+(?:on|about|for|to)\s+/i,
  /^notas?\s+(?:sobre|para|de)\s+/i,
  /^evidence\s+and\s+examples?/i,
  /^evidencias?\s+y\s+ejemplos?/i,
  /^consequences?\s+if/i,
  /^consecuencias?\s+si/i,
  /^why\s+(?:it\s+)?(?:hurts|matters)/i,
  /^current\s+state\b/i,
  /^estado\s+actual\b/i,
  // Closing meta-items observados en producción
  /^next\s+steps?\b/i,
  /^pr[óo]ximos?\s+pasos?\b/i,
  /^pasos?\s+siguientes?\b/i,
  /^to\s+(?:do|note|consider|review)\s*:/i,
  /^pendientes?\s*:/i,
  /^por\s+(?:hacer|revisar|considerar)\s*:/i,
  /^summary\s*:/i,
  /^resumen\s*:/i,
  /^conclusion\s*:/i,
  /^conclusi[óo]n\s*:/i,
  /^validation\s+(?:criteria|notes?)\s*:/i,
  /^criterios?\s+de\s+validaci[óo]n/i,
  // Items ridículamente cortos o solo markup
  /^\*+$/,
  /^#+$/,
]

function isMetaItem(text: string): boolean {
  return _META_ITEM_PATTERNS.some((re) => re.test(text.trim()))
}

/** Parser para checklist — el agente devuelve `{text, done}[]`. */
function parseChecklistPayload(payload: string): unknown {
  const parsed = tryParseJsonArray(payload)
  if (parsed) {
    const items = parsed
      .map((it) => {
        if (typeof it === 'string') return { text: it, done: false }
        if (it && typeof it === 'object') {
          const obj = it as Record<string, unknown>
          const text =
            typeof obj['text'] === 'string'
              ? obj['text']
              : typeof obj['item'] === 'string'
                ? obj['item']
                : null
          if (text === null) return null
          const done = obj['done'] === true || obj['checked'] === true
          return { text, done }
        }
        return null
      })
      .filter((x): x is { text: string; done: boolean } => x !== null)
      .filter((x) => x.text.length > 0 && !isMetaItem(x.text))
    return items
  }
  // Fallback: tratar como list y mapear a {text, done: false}.
  const strs = parseListPayload(payload)
  return strs.map((text) => ({ text, done: false }))
}

/** Parser para `structured` (JSON libre) — el agente devuelve un
 * JSON value (object o array). El editor de structured persiste y
 * muestra el JSON crudo, así que devolvemos el value parseado tal cual.
 *
 * Si el LLM falla al producir JSON parseable, devolvemos un objeto
 * con el payload como string en una key `_raw_response` — al menos
 * el user tiene el contenido para revisar manualmente. */
function parseStructuredPayload(payload: string): unknown {
  const text = payload.trim()

  // 1. JSON puro (object o array)
  if (text.startsWith('{') || text.startsWith('[')) {
    try {
      return JSON.parse(text)
    } catch {
      // Cae al fence
    }
  }

  // 2. JSON dentro de fenced ```json ... ```
  const fenced = /```(?:json)?\s*([{[][\s\S]*?[}\]])\s*```/i.exec(text)
  if (fenced) {
    try {
      return JSON.parse(fenced[1] ?? '')
    } catch {
      // Cae al fallback
    }
  }

  // 3. Brace-match desde el primer `{` o `[` (tolera trailing/leading)
  const objStart = text.indexOf('{')
  const arrStart = text.indexOf('[')
  const start =
    objStart >= 0 && (arrStart < 0 || objStart < arrStart) ? objStart : arrStart
  if (start >= 0) {
    const slice = extractFirstJsonValue(text, start)
    if (slice) {
      try {
        return JSON.parse(slice)
      } catch {
        // continúa al fallback
      }
    }
  }

  // 4. Fallback: devolver el payload como objeto debug
  return { _raw_response: text }
}

/** Encuentra el primer JSON value balanceado desde `start` (que debe
 * apuntar a `{` o `[`). Respeta strings con escape. */
function extractFirstJsonValue(text: string, start: number): string | null {
  const opener = text[start]
  const closer = opener === '{' ? '}' : opener === '[' ? ']' : null
  if (!closer) return null
  let depth = 0
  let inString = false
  let escape = false
  for (let i = start; i < text.length; i++) {
    const c = text[i]
    if (escape) {
      escape = false
      continue
    }
    if (c === '\\') {
      escape = true
      continue
    }
    if (c === '"') {
      inString = !inString
      continue
    }
    if (inString) continue
    if (c === opener) depth += 1
    else if (c === closer) {
      depth -= 1
      if (depth === 0) return text.slice(start, i + 1)
    }
  }
  return null
}

/** Parser para code_block — el agente devuelve `{language, code}`.
 *
 * Estrategia (3 niveles):
 *   1. JSON puro `{language: "...", code: "..."}` — path principal.
 *   2. JSON dentro de fenced ```json ... ``` — LLM agregó fence.
 *   3. Fallback: tratar el payload entero como `code` con
 *      `language = defaultLanguage`. Útil si el LLM ignoró el formato
 *      JSON y devolvió código plano.
 *
 * También limpia: si el `code` viene envuelto en fence (```sql...```)
 * literal, lo strip — el editor del frontend ya muestra el lenguaje
 * por separado.
 */
function parseCodeBlockPayload(
  payload: string,
  defaultLanguage: string,
): { language: string; code: string } {
  const text = payload.trim()

  // 1. JSON puro
  if (text.startsWith('{') && text.endsWith('}')) {
    try {
      const parsed = JSON.parse(text)
      if (parsed && typeof parsed === 'object') {
        const obj = parsed as Record<string, unknown>
        const lang =
          typeof obj['language'] === 'string'
            ? obj['language'].trim() || defaultLanguage
            : defaultLanguage
        const code =
          typeof obj['code'] === 'string'
            ? stripCodeFence(obj['code'])
            : ''
        return { language: lang, code }
      }
    } catch {
      // Cae al fenced
    }
  }

  // 2. JSON dentro de fenced
  const fenced = /```(?:json)?\s*(\{[\s\S]*?\})\s*```/i.exec(text)
  if (fenced) {
    try {
      const parsed = JSON.parse(fenced[1] ?? '')
      if (parsed && typeof parsed === 'object') {
        const obj = parsed as Record<string, unknown>
        const lang =
          typeof obj['language'] === 'string'
            ? obj['language'].trim() || defaultLanguage
            : defaultLanguage
        const code =
          typeof obj['code'] === 'string'
            ? stripCodeFence(obj['code'])
            : ''
        return { language: lang, code }
      }
    } catch {
      // Cae al fallback
    }
  }

  // 3. Fallback: payload como código crudo. Si viene en una fence
  // markdown (```lang\n...\n```) extraemos lang + code.
  const codeFence = /^```(\S*)\s*\n([\s\S]*?)\n?```\s*$/m.exec(text)
  if (codeFence) {
    return {
      language: codeFence[1] || defaultLanguage,
      code: codeFence[2] ?? '',
    }
  }
  return { language: defaultLanguage, code: text }
}

/** Quita una sola capa de fence markdown del texto si existe. Útil
 * para cuando el LLM puso doble fence (la del response + la del
 * `code`). */
function stripCodeFence(s: string): string {
  const m = /^```(?:\S*)\s*\n([\s\S]*?)\n?```\s*$/m.exec(s.trim())
  return m ? (m[1] ?? '') : s
}

/** Parser para Mermaid (Sprint AI-1.e — Fase 4).
 *
 * El backend declara en el system prompt que el output es un JSON
 * object `{"code": "<mermaid source>"}` sin fences. Este parser tiene
 * 4 niveles de fallback:
 *
 *   1. JSON puro `{"code": "..."}` → extrae `code`.
 *   2. JSON dentro de fence `\`\`\`json {...} \`\`\`` → extrae `code`.
 *   3. Fence mermaid `\`\`\`mermaid\n...\n\`\`\`` → devuelve el body.
 *   4. Raw passthrough — el DiagramEditor lo trata como string.
 *
 * El value de Mermaid en el editor puede ser string raw o `{source}`;
 * el `parseDiagramSource` (SectionRenderer.tsx) acepta ambos. Aquí
 * persistimos como string raw — más simple y compatible.
 */
function parseMermaidPayload(payload: string): string {
  const text = payload.trim()

  // 1. JSON puro
  if (text.startsWith('{') && text.endsWith('}')) {
    try {
      const parsed = JSON.parse(text)
      if (parsed && typeof parsed === 'object') {
        const obj = parsed as Record<string, unknown>
        if (typeof obj['code'] === 'string') {
          return stripCodeFence(obj['code']).trim()
        }
      }
    } catch {
      // cae al siguiente nivel
    }
  }

  // 2. JSON dentro de fence
  const jsonFence = /```(?:json)?\s*(\{[\s\S]*?\})\s*```/i.exec(text)
  if (jsonFence) {
    try {
      const parsed = JSON.parse(jsonFence[1] ?? '')
      if (parsed && typeof parsed === 'object') {
        const obj = parsed as Record<string, unknown>
        if (typeof obj['code'] === 'string') {
          return stripCodeFence(obj['code']).trim()
        }
      }
    } catch {
      // cae al siguiente nivel
    }
  }

  // 3. Fence mermaid
  const mermaidFence = /^```mermaid\s*\n([\s\S]*?)\n?```\s*$/m.exec(text)
  if (mermaidFence) {
    return (mermaidFence[1] ?? '').trim()
  }

  // 4. Raw passthrough
  return text
}

/** Parser para structured_table — soporta DOS shapes de entrada:
 *
 *   1. **JSON array** (output del section_draft_agent): la ruta
 *      canónica. Cada row es un objeto con keys = `column.key`.
 *   2. **Markdown table** (output del comment_review_agent — el flujo
 *      "Subsanar con IA"): cada row es una línea `| celda | celda | ...`
 *      con un header en la primera línea y un separador `|---|...` en
 *      la segunda. Los headers son LABELS (en el idioma del
 *      doc), no keys — hay que mapear contra `column.label` ↔
 *      `column.key`.
 *
 * Validación defensiva:
 *   · Si no es objeto plano, descartar.
 *   · Filtrar keys EXTRAS que no estén en `columns` declaradas.
 *   · Forzar string a values text/enum/date.
 *   · Omitir columnas autonum (las llena el frontend).
 *
 * Las columnas v2 traen `label` + `optionLabels` inline (vía
 * `normalizeTableSchema`). Para enum, el markdown table contiene
 * labels localizados ("Quincenal"), y necesitamos persistir el value
 * estable ("quincenal" / `option_value` correspondiente): para ese caso el
 * parser hace el reverse-mapping con `optionLabels`. El JSON de la IA ya
 * llega con códigos (OT-0042). */
function parseStructuredTablePayload(
  payload: string,
  columns: unknown[],
): Record<string, string>[] {
  // Set de keys válidas (excluyendo autonum, que el FE asigna)
  const validKeys = new Set<string>()
  const allKeys = new Set<string>()
  // Map label → key para soportar headers de markdown table.
  const labelToKey = new Map<string, string>()
  // Map por columna: label → value (para reverse-lookup de enums).
  const enumLabelToValue: Record<string, Record<string, string>> = {}
  for (const c of columns) {
    if (c && typeof c === 'object') {
      const obj = c as Record<string, unknown>
      const key = typeof obj['key'] === 'string' ? obj['key'] : null
      const type = typeof obj['type'] === 'string' ? obj['type'] : null
      if (!key) continue
      allKeys.add(key)
      if (type !== 'autonum') validKeys.add(key)
      // Map labels para markdown table parsing.
      const label = typeof obj['label'] === 'string' ? obj['label'] : null
      if (label) labelToKey.set(label.toLowerCase(), key)
      // El key mismo también es un match válido (por si el LLM usó key
      // directamente como header en lugar del label).
      labelToKey.set(key.toLowerCase(), key)
      const optionLabels = obj['optionLabels']
      if (optionLabels && typeof optionLabels === 'object') {
        const reverse: Record<string, string> = {}
        for (const [val, lbl] of Object.entries(
          optionLabels as Record<string, string>,
        )) {
          if (typeof lbl === 'string' && lbl) {
            reverse[lbl.toLowerCase()] = val
          }
        }
        enumLabelToValue[key] = reverse
      }
    }
  }

  // Path 1: JSON array (preferido). Los códigos de enum ya vienen resueltos
  // por el backend (OT-0042): lo que no sea código se deja tal cual y el
  // editor lo marca como inválido.
  const parsed = tryParseJsonArray(payload)
  // Path 2: si no parsea, markdown table (sugerencias de la IA de
  // comentarios anteriores al `suggested_value`). Ese markdown lo serializa
  // el backend con etiquetas, así que aquí sí se vuelve de etiqueta a código.
  const fromMarkdown = !(parsed && Array.isArray(parsed))
  const rawRows: Array<Record<string, unknown>> = fromMarkdown
    ? parseMarkdownTablePayload(payload, labelToKey)
    : ((parsed as unknown[]).filter(
        (r) => r && typeof r === 'object' && !Array.isArray(r),
      ) as Array<Record<string, unknown>>)

  if (rawRows.length === 0) return []

  const result: Record<string, string>[] = []
  for (const src of rawRows) {
    const out: Record<string, string> = {}
    for (const k of allKeys) {
      out[k] = ''
    }
    for (const [k, v] of Object.entries(src)) {
      if (!validKeys.has(k)) continue
      let coerced = ''
      if (v === null || v === undefined) {
        coerced = ''
      } else if (typeof v === 'string') {
        coerced = v
      } else if (typeof v === 'number' || typeof v === 'boolean') {
        coerced = String(v)
      } else {
        coerced = JSON.stringify(v)
      }
      // Markdown table: label localizado ("Quincenal") → value ("quincenal").
      const enumMap = fromMarkdown ? enumLabelToValue[k] : undefined
      if (enumMap && coerced) {
        const looked = enumMap[coerced.toLowerCase()]
        if (looked) coerced = looked
      }
      out[k] = coerced
    }
    result.push(out)
  }
  return result
}

/** Parser de markdown tables al shape `{key: value}[]` usando el map
 * `label → key` derivado de las columnas del schema.
 *
 * Tolera:
 *   - Texto trailing/leading (preámbulos del LLM tipo "Aquí va...").
 *   - Espacios en los pipes (`|  cell  |`).
 *   - Línea separadora opcional (`|---|---|`).
 *   - Headers que matchean por label O por key (insensible a mayúsculas).
 *
 * Devuelve `[]` si no encuentra una tabla válida — el caller maneja el
 * caso vacío con su propia validación. */
function parseMarkdownTablePayload(
  payload: string,
  labelToKey: Map<string, string>,
): Array<Record<string, unknown>> {
  if (!payload || labelToKey.size === 0) return []
  const lines = payload
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.startsWith('|') && l.endsWith('|'))
  if (lines.length < 2) return []

  function splitRow(line: string): string[] {
    // Remove leading/trailing pipe y split por pipe.
    const inner = line.replace(/^\||\|$/g, '')
    return inner.split('|').map((c) => c.trim())
  }

  // Localizar la línea de header: la primera cuyo split mapea AL MENOS
  // 1 celda a una key válida.
  let headerIdx = -1
  let columnKeys: (string | null)[] = []
  for (let i = 0; i < lines.length; i++) {
    const candidate = lines[i]
    if (!candidate) continue
    const cells = splitRow(candidate)
    const mapped = cells.map(
      (cell) => labelToKey.get(cell.toLowerCase()) ?? null,
    )
    if (mapped.some((k) => k !== null)) {
      headerIdx = i
      columnKeys = mapped
      break
    }
  }
  if (headerIdx < 0) return []

  // Skip header + (opcional) separadora `|---|---|`.
  let bodyStart = headerIdx + 1
  const sepLine = lines[bodyStart]
  if (sepLine && /^\|?[\s|:-]+\|?$/.test(sepLine)) {
    bodyStart += 1
  }

  const rows: Array<Record<string, unknown>> = []
  for (let i = bodyStart; i < lines.length; i++) {
    const line = lines[i]
    if (!line) continue
    const cells = splitRow(line)
    const row: Record<string, unknown> = {}
    for (let c = 0; c < cells.length && c < columnKeys.length; c++) {
      const key = columnKeys[c]
      if (!key) continue
      row[key] = cells[c] ?? ''
    }
    if (Object.keys(row).length > 0) rows.push(row)
  }
  return rows
}

/** Parser para key_value_grid — soporta DOS shapes:
 *   1. JSON array `[{key, value}, ...]` (output del section_draft_agent).
 *   2. Markdown table `| Key | Value |\n|---|---|\n| Sponsor | Acme |`
 *      (output del comment_review_agent — "Subsanar con IA").
 *
 * El parser intenta JSON primero; si falla, parsea markdown table con
 * 2 columnas (la primera = key, la segunda = value). Tolera headers
 * arbitrarios — solo importa la posición. */
function parseKvGridPayload(payload: string): unknown {
  const parsed = tryParseJsonArray(payload)
  if (parsed) {
    return parsed
      .map((it) => {
        if (it && typeof it === 'object') {
          const obj = it as Record<string, unknown>
          const k =
            typeof obj['key'] === 'string'
              ? obj['key']
              : typeof obj['name'] === 'string'
                ? obj['name']
                : null
          const v =
            typeof obj['value'] === 'string'
              ? obj['value']
              : obj['value'] !== undefined
                ? String(obj['value'])
                : null
          if (k && v) return { key: k, value: v }
        }
        return null
      })
      .filter(
        (x): x is { key: string; value: string } =>
          x !== null && !isMetaItem(x.key),
      )
  }
  // Fallback markdown table: el comment_review_agent produce
  // `| Key | Value |\n|---|---|\n| Sponsor | Acme |`.
  const lines = payload
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.startsWith('|') && l.endsWith('|'))
  if (lines.length >= 2) {
    function splitRow(line: string): string[] {
      const inner = line.replace(/^\||\|$/g, '')
      return inner.split('|').map((c) => c.trim())
    }
    // Skip header + separator.
    let bodyStart = 1
    const sepLine = lines[bodyStart]
    if (sepLine && /^\|?[\s|:-]+\|?$/.test(sepLine)) {
      bodyStart += 1
    }
    const out: Array<{ key: string; value: string }> = []
    for (let i = bodyStart; i < lines.length; i++) {
      const line = lines[i]
      if (!line) continue
      const cells = splitRow(line)
      if (cells.length < 2) continue
      const k = cells[0]?.trim()
      const v = cells[1]?.trim()
      if (k && v && !isMetaItem(k)) {
        out.push({ key: k, value: v })
      }
    }
    if (out.length > 0) return out
  }
  // Fallback vacío — el user llenará manualmente.
  return []
}

/** Parser JSON array tolerante a basura trailing/leading.
 *
 * Estrategia (3 niveles, en orden):
 *
 *   1. **JSON puro**: el texto ENTERO es un JSON array válido. Camino
 *      ideal — el LLM siguió el contrato.
 *
 *   2. **Fenced code block**: ```json [...] ``` o ``` [...] ```. El
 *      LLM agregó fence pese a que el prompt dice no hacerlo.
 *
 *   3. **Brace-matching desde el primer `[`**: el LLM agregó texto
 *      antes Y/O después del array (preámbulo "Here is the JSON
 *      array:" o coda "Phase 0 relies on these stakeholders..."). Lo
 *      extraemos haciendo balanced bracket scan respetando strings.
 *      Detectado en validación AI-1.d.b: el modelo adjunta a veces un
 *      párrafo de cierre tras el `]`.
 *
 *   Si nada de lo anterior produce array válido, retorna null y el
 *   caller cae a su fallback (split-por-líneas).
 */
function tryParseJsonArray(payload: string): unknown[] | null {
  const text = payload.trim()

  // 1. JSON puro
  if (text.startsWith('[') && text.endsWith(']')) {
    try {
      const parsed = JSON.parse(text)
      if (Array.isArray(parsed)) return parsed
    } catch {
      // continúa
    }
  }

  // 2. Fenced code block
  const fenced = /```(?:json)?\s*(\[[\s\S]*?\])\s*```/i.exec(text)
  if (fenced) {
    try {
      const parsed = JSON.parse(fenced[1] ?? '')
      if (Array.isArray(parsed)) return parsed
    } catch {
      // continúa
    }
  }

  // 3. Brace-matching desde el primer `[`: tolera texto antes/después.
  const extracted = extractFirstJsonArray(text)
  if (extracted !== null) {
    try {
      const parsed = JSON.parse(extracted)
      if (Array.isArray(parsed)) return parsed
    } catch {
      // null final
    }
  }
  return null
}

/** Encuentra el primer `[...]` balanceado en el texto, respetando
 * strings con comillas escapadas. Retorna el substring o null si no
 * hay un array bien balanceado. */
function extractFirstJsonArray(text: string): string | null {
  const start = text.indexOf('[')
  if (start < 0) return null
  let depth = 0
  let inString = false
  let escape = false
  for (let i = start; i < text.length; i++) {
    const c = text[i]
    if (escape) {
      escape = false
      continue
    }
    if (c === '\\') {
      escape = true
      continue
    }
    if (c === '"') {
      inString = !inString
      continue
    }
    if (inString) continue
    if (c === '[') {
      depth += 1
    } else if (c === ']') {
      depth -= 1
      if (depth === 0) {
        return text.slice(start, i + 1)
      }
    }
  }
  return null
}

interface SectionEditorProps {
  meta: TemplateSectionMeta
  title: string
  value: unknown
  onChange: (value: unknown) => void
  disabled?: boolean
  /** Placeholder localizado de `i18nSection.placeholder`. Lo usa el
   * editor de rich_text para mostrar texto en gris cuando está vacío. */
  placeholder?: string
  /** Texto de ayuda localizado (`i18nSection.help_text`). Se renderiza
   * debajo del editor en estilo subtle. */
  helpText?: string
  /** Si se pasa, habilita el botón "Generar con IA" en el header. El
   * artifact debe estar en estado draft (el padre garantiza esto). */
  ai?: {
    artifactId: string
    /** Versión draft activa del artifact. Se usa SOLO para invalidar
     * el cache de citas de TanStack Query después de persistir el set
     * nuevo (ADR-0001 Tier 1). Si no se pasa, la persistencia sigue
     * funcionando pero el viewer no se refresca hasta el próximo
     * fetch. */
    versionId?: string
    projectLanguage: string
    /** Secciones del template (excluyendo la activa) disponibles para
     * referenciar como tokens `{{section_id.value}}` en el modal de IA.
     * El padre las construye aplanando el árbol del template + i18n. */
    availableTokenSections?: AvailableTokenSection[]
    /** Sprint AI-1.f: path desde root al caller, incluyendo índices de
     * instancias repetibles. El SectionTreeRenderer lo construye al
     * recursar. Permite al backend resolver casos 2/3 (instancia
     * activa) en lugar de caer al caso 4 (array completo). */
    currentPath?: PathSegment[]
    /** Sprint AI-1.f.bug2: callback que el padre ejecuta antes de
     * abrir el modal de IA — típicamente `saveMutation.mutateAsync()`
     * para persistir cambios pendientes en BD. Sin esto, la IA leería
     * el current_version stale (sin los cambios del editor) y los
     * tokens / context_sections resolverían vacío. Si el save falla,
     * el modal NO se abre y el error se propaga al caller. */
    onBeforeAiOpen?: () => Promise<void>
  }
}

export function SectionEditor({
  meta,
  title,
  value,
  onChange,
  disabled = false,
  placeholder,
  helpText,
  ai,
}: SectionEditorProps) {
  const { t } = useTranslation('projects')
  const { t: tAi } = useTranslation('ai')
  const apiErrorMessage = useApiErrorMessage()
  const [aiOpen, setAiOpen] = useState(false)
  // Sprint AI-1.f.bug2: lock visual del botón mientras corre el
  // auto-save antes de abrir el modal. Sin esto el user puede dar doble
  // clic durante el save y disparar dos generaciones.
  const [aiOpening, setAiOpening] = useState(false)
  const handleOpenAi = async () => {
    if (ai?.onBeforeAiOpen) {
      setAiOpening(true)
      try {
        await ai.onBeforeAiOpen()
      } catch {
        setAiOpening(false)
        return
      }
      setAiOpening(false)
    }
    setAiOpen(true)
  }

  const aiSupported = AI_SUPPORTED_TYPES.has(meta.type)
  const showAiButton = !!ai && !disabled
  const queryClient = useQueryClient()
  const { accessToken } = useAccessToken()
  // OT-0049 — observaciones de la verificación de IA de esta sección. Una
  // sola consulta por versión (misma query key para todas las secciones).
  const groundingsQuery = useVersionGroundings({
    artifactId: ai?.artifactId ?? null,
    versionId: ai?.versionId ?? null,
  })
  const instanceKey = buildInstanceKey(meta.section_id, ai?.currentPath ?? [])
  const sectionGrounding =
    groundingsQuery.data?.find(
      (g) => g.section_id === meta.section_id && g.instance_key === instanceKey,
    ) ?? null

  /** OT-0049 — guarda las citas (con su evidencia) y el juicio por
   * oración de la corrida que el usuario aceptó, y refresca el viewer. El
   * servidor las toma de la corrida; aquí sólo se dice dónde quedó el
   * texto. Si falla, el contenido ya está en el editor pero sus `[N]` no
   * tendrían respaldo: se avisa para que regenere la sección. Si falla
   * porque la versión dejó de ser editable (OT-0064), el editor se bloquea
   * y el aviso dice por qué. */
  const persistDraftRun = async (draftRunId: string): Promise<void> => {
    if (!ai || !accessToken) return
    try {
      await applySectionDraftRunCitations(
        accessToken,
        ai.artifactId,
        draftRunId,
        buildInstanceKey(meta.section_id, ai.currentPath ?? []),
      )
      if (ai.versionId) {
        void invalidateVersionAiMetadata(queryClient, ai.versionId)
      }
    } catch (err) {
      if (isVersionLockError(err)) {
        markVersionLocked(queryClient, ai.artifactId, ai.versionId ?? null)
        pushToast({ kind: 'critical', message: apiErrorMessage(err), duration: 6000 })
        return
      }
      pushToast({ kind: 'review', message: tAi('citations.persist_failed') })
    }
  }

  const handleAiAccept = (payload: string, draftRunId: string) => {
    // ADR-0001 Tier 1 + OT-0049 — las citas del draft se guardan desde la
    // corrida en el servidor para que los `[N]` sean verificables.
    void persistDraftRun(draftRunId)

    // Doc 24 §3 — para structured_table v2, las columnas viven en
    // `meta.table_config.columns`, no en `meta.columns`. Normalizamos
    // con el adapter para que `parseStructuredTablePayload` reciba el
    // shape lite que espera (con `key` + `type` por columna). Sin esto,
    // el conversor llegaba con `cols=[]` y producía rows vacíos al
    // aplicar "Reemplazar contenido".
    const projectLanguage = ai?.projectLanguage ?? 'es'
    let columns: unknown[] | undefined
    if (meta.type === 'structured_table') {
      const normalized = normalizeTableSchema(
        meta as unknown as Parameters<typeof normalizeTableSchema>[0],
        projectLanguage,
      )
      columns = normalized.columns
    } else {
      columns = (meta as unknown as { columns?: unknown[] }).columns
    }
    const defaultLanguage = (
      meta as unknown as { default_language?: string }
    ).default_language
    const opts: { columns?: unknown[]; defaultLanguage?: string } = {}
    if (columns !== undefined) opts.columns = columns
    if (defaultLanguage !== undefined) opts.defaultLanguage = defaultLanguage
    onChange(convertAiPayloadForSection(payload, meta.type, opts))
  }

  return (
    <article className="bg-surface border border-line rounded-md p-5">
      <header className="mb-3 flex items-baseline gap-3 flex-wrap">
        <h3 className="font-display text-lg font-semibold text-ink">{title}</h3>
        {meta.required ? (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-surface-muted text-ink-subtle">
            {t('official_viewer.section_required_badge')}
          </span>
        ) : null}
        {showAiButton && (
          <button
            type="button"
            onClick={handleOpenAi}
            disabled={!aiSupported || aiOpening}
            title={
              aiSupported
                ? tAi('section_button.tooltip_default')
                : tAi('section_button.tooltip_disabled_unsupported')
            }
            className="inline-flex items-center gap-1 rounded border border-brand/30 px-2 py-0.5 text-[11px] font-medium text-brand hover:bg-brand/5 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Sparkles className="h-3 w-3" aria-hidden="true" />
            {tAi('section_button.label')}
          </button>
        )}
        <span className="ml-auto font-mono text-[10px] text-ink-subtle">
          {meta.section_id}
        </span>
      </header>

      {ai?.versionId && sectionGrounding && sectionGrounding.flagged.length > 0 ? (
        <div className="mb-2">
          <SectionObservations
            grounding={sectionGrounding}
            artifactId={ai.artifactId}
            versionId={ai.versionId}
            canReview={!disabled}
          />
        </div>
      ) : null}

      {!isSupportedBlockType(meta.type) ? (
        <UnsupportedEditor type={String(meta.type)} />
      ) : meta.type === 'rich_text' ? (
        <RichTextEditor
          value={value as never}
          onChange={(next) => onChange(next)}
          disabled={disabled}
          {...(placeholder ? { placeholder } : {})}
        />
      ) : meta.type === 'plain_text' ? (
        <TextareaEditor
          value={value}
          onChange={onChange}
          disabled={disabled}
          rows={4}
        />
      ) : meta.type === 'bullet_list' ? (
        <ListEditor value={value} onChange={onChange} disabled={disabled} />
      ) : meta.type === 'numbered_list' ? (
        <ListEditor value={value} onChange={onChange} disabled={disabled} />
      ) : meta.type === 'checklist' ? (
        <ChecklistEditor
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      ) : meta.type === 'key_value_grid' ? (
        <KeyValueGridEditor
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      ) : meta.type === 'structured' ? (
        <StructuredEditor
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      ) : meta.type === 'code_block' ? (
        <CodeBlockEditor
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      ) : meta.type === 'fixed_heading' ? (
        <FixedHeadingEditor />
      ) : meta.type === 'fixed_block' ? (
        <FixedBlockEditor />
      ) : meta.type === 'structured_table' ? (
        <StructuredTableEditor
          value={value}
          meta={meta}
          onChange={onChange}
          disabled={disabled}
          locale={ai?.projectLanguage ?? 'es'}
        />
      ) : isMermaidType(meta.type) ? (
        <DiagramEditor
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={MERMAID_PLACEHOLDERS[meta.type]}
        />
      ) : null}

      {helpText ? (
        <p className="mt-2 text-[11px] text-ink-subtle font-body">
          {helpText}
        </p>
      ) : null}

      {ai && aiSupported && (
        <SectionDraftDialog
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          artifactId={ai.artifactId}
          sectionId={meta.section_id}
          sectionTitle={title}
          sectionType={meta.type}
          projectLanguage={ai.projectLanguage}
          currentValue={value}
          availableTokenSections={ai.availableTokenSections ?? []}
          currentPath={ai.currentPath ?? []}
          versionId={ai.versionId ?? null}
          onAccept={handleAiAccept}
        />
      )}
    </article>
  )
}

// ─── Unsupported ────────────────────────────────────────────────────────────
function UnsupportedEditor({ type }: { type: string }) {
  const { t } = useTranslation('projects')
  return (
    <div className="rounded-md border border-review/40 bg-review-soft/40 p-3 flex items-start gap-2">
      <ShieldAlert className="w-4 h-4 text-review mt-0.5 shrink-0" />
      <p className="font-body text-xs text-ink-muted">
        {t('official_viewer.unsupported_block_type', { type })}
      </p>
    </div>
  )
}

// ─── Textarea (rich_text + plain_text) ─────────────────────────────────────
function TextareaEditor({
  value,
  onChange,
  disabled,
  rows = 6,
}: {
  value: unknown
  onChange: (value: unknown) => void
  disabled: boolean
  rows?: number
}) {
  const text = typeof value === 'string' ? value : ''
  return (
    <textarea
      value={text}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      rows={rows}
      spellCheck
      className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-body leading-relaxed focus:outline-none focus:border-brand resize-y disabled:opacity-50"
    />
  )
}

// ─── List editor (bullet / numbered) ───────────────────────────────────────
function ListEditor({
  value,
  onChange,
  disabled,
}: {
  value: unknown
  onChange: (value: unknown) => void
  disabled: boolean
}) {
  const { t } = useTranslation('projects')
  // Normaliza el value a `string[]` para el editor. Strings extranjeros
  // ({text} / {value}) ya están manejados por el viewer; aquí
  // simplificamos a strings porque todos los bullet_list seed son
  // arrays de strings simples.
  const items = normalizeListItems(value)

  function setItems(next: string[]) {
    onChange(next)
  }

  function update(idx: number, v: string) {
    const next = [...items]
    next[idx] = v
    setItems(next)
  }
  function add() {
    setItems([...items, ''])
  }
  function remove(idx: number) {
    setItems(items.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-2">
      {items.length === 0 ? (
        <p className="font-body text-sm text-ink-subtle italic">
          {t('official_editor.list.empty_hint')}
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="text-ink-subtle mt-2.5 text-sm shrink-0 font-mono">
                {idx + 1}.
              </span>
              <input
                type="text"
                value={item}
                onChange={(e) => update(idx, e.target.value)}
                disabled={disabled}
                placeholder={t('official_editor.list.item_placeholder')}
                className="flex-1 bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => remove(idx)}
                disabled={disabled}
                aria-label={t('official_editor.list.remove_item')}
                title={t('official_editor.list.remove_item')}
                className="p-2 rounded-md text-ink-subtle hover:text-critical hover:bg-critical-soft disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={add}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 text-xs text-brand hover:underline disabled:opacity-50"
      >
        <Plus className="w-3.5 h-3.5" />
        {t('official_editor.list.add_item')}
      </button>
    </div>
  )
}

// ─── Checklist editor (items con texto + checkbox done) ────────────────────
interface ChecklistDraftItem {
  text: string
  done: boolean
}

function normalizeChecklistItems(value: unknown): ChecklistDraftItem[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      if (typeof item === 'string') {
        return { text: item, done: false } satisfies ChecklistDraftItem
      }
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>
        return {
          text: typeof obj['text'] === 'string' ? obj['text'] : '',
          done: obj['done'] === true,
        } satisfies ChecklistDraftItem
      }
      return { text: '', done: false } satisfies ChecklistDraftItem
    })
}

function ChecklistEditor({
  value,
  onChange,
  disabled,
}: {
  value: unknown
  onChange: (value: unknown) => void
  disabled: boolean
}) {
  const { t } = useTranslation('projects')
  const items = normalizeChecklistItems(value)

  function setAll(next: ChecklistDraftItem[]) {
    onChange(next)
  }
  function update(idx: number, patch: Partial<ChecklistDraftItem>) {
    setAll(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)))
  }
  function add() {
    setAll([...items, { text: '', done: false }])
  }
  function remove(idx: number) {
    setAll(items.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={item.done}
            onChange={(e) => update(idx, { done: e.target.checked })}
            disabled={disabled}
            aria-label={t('official_editor.list.toggle_done')}
            className="accent-official"
          />
          <input
            type="text"
            value={item.text}
            onChange={(e) => update(idx, { text: e.target.value })}
            disabled={disabled}
            className={
              item.done
                ? 'flex-1 bg-canvas border border-line rounded-md px-3 py-1.5 text-sm line-through text-ink-muted disabled:opacity-50'
                : 'flex-1 bg-canvas border border-line rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-brand disabled:opacity-50'
            }
          />
          <button
            type="button"
            onClick={() => remove(idx)}
            disabled={disabled}
            aria-label={t('official_editor.list.remove_item')}
            className="p-1.5 rounded text-ink-subtle hover:text-critical hover:bg-critical-soft disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 text-xs text-brand hover:underline disabled:opacity-50"
      >
        <Plus className="w-3.5 h-3.5" />
        {t('official_editor.list.add_item')}
      </button>
    </div>
  )
}

// ─── Key-value grid editor ─────────────────────────────────────────────────
interface KvPair {
  key: string
  value: string
}

function KeyValueGridEditor({
  value,
  onChange,
  disabled,
}: {
  value: unknown
  onChange: (value: unknown) => void
  disabled: boolean
}) {
  const { t } = useTranslation('projects')
  const pairs: KvPair[] = normalizeKvPairs(value)

  function setPairs(next: KvPair[]) {
    onChange(next)
  }
  function update(idx: number, patch: Partial<KvPair>) {
    const next = pairs.map((p, i) => (i === idx ? { ...p, ...patch } : p))
    setPairs(next)
  }
  function add() {
    setPairs([...pairs, { key: '', value: '' }])
  }
  function remove(idx: number) {
    setPairs(pairs.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-2">
      {pairs.length === 0 ? (
        <p className="font-body text-sm text-ink-subtle italic">
          {t('official_editor.key_value.empty_hint')}
        </p>
      ) : (
        <div className="space-y-2">
          {pairs.map((pair, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[200px_1fr_auto] gap-2 items-center"
            >
              <input
                type="text"
                value={pair.key}
                onChange={(e) => update(idx, { key: e.target.value })}
                disabled={disabled}
                placeholder={t('official_editor.key_value.key_placeholder')}
                className="bg-canvas border border-line rounded-md px-3 py-2 text-sm font-medium focus:outline-none focus:border-brand disabled:opacity-50"
              />
              <input
                type="text"
                value={pair.value}
                onChange={(e) => update(idx, { value: e.target.value })}
                disabled={disabled}
                placeholder={t('official_editor.key_value.value_placeholder')}
                className="bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => remove(idx)}
                disabled={disabled}
                aria-label={t('official_editor.key_value.remove_pair')}
                title={t('official_editor.key_value.remove_pair')}
                className="p-2 rounded-md text-ink-subtle hover:text-critical hover:bg-critical-soft disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={add}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 text-xs text-brand hover:underline disabled:opacity-50"
      >
        <Plus className="w-3.5 h-3.5" />
        {t('official_editor.key_value.add_pair')}
      </button>
    </div>
  )
}

// ─── Structured (JSON freehand) ────────────────────────────────────────────
function StructuredEditor({
  value,
  onChange,
  disabled,
}: {
  value: unknown
  onChange: (value: unknown) => void
  disabled: boolean
}) {
  const { t } = useTranslation('projects')
  // Edición libre del JSON. Parseo al onBlur — si parsea, propaga el
  // objeto; si no, mantenemos el string crudo y dejamos al backend
  // validar el schema cuando el user mande el draft.
  const text = typeof value === 'string' ? value : safeJsonStringify(value)

  return (
    <div className="space-y-2">
      <textarea
        value={text}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => {
          // Si el usuario terminó de editar y el JSON parsea, sustituye
          // el value por el objeto. Si no, deja la string para no perder
          // edición.
          const raw = e.target.value
          try {
            const parsed = JSON.parse(raw)
            onChange(parsed)
          } catch {
            onChange(raw)
          }
        }}
        disabled={disabled}
        rows={10}
        spellCheck={false}
        className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:border-brand resize-y disabled:opacity-50"
      />
      <p className="text-[11px] text-ink-subtle italic">
        {t('official_editor.structured.json_help')}
      </p>
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function normalizeListItems(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>
        if (typeof obj['text'] === 'string') return obj['text']
        if (typeof obj['value'] === 'string') return obj['value']
      }
      return null
    })
    .filter((s): s is string => s !== null)
}

function normalizeKvPairs(value: unknown): KvPair[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>
        const k =
          typeof obj['key'] === 'string'
            ? obj['key']
            : typeof obj['name'] === 'string'
              ? obj['name']
              : null
        const v =
          typeof obj['value'] === 'string'
            ? obj['value']
            : obj['value'] !== undefined
              ? String(obj['value'])
              : null
        if (k !== null && v !== null) return { key: k, value: v }
      }
      return null
    })
    .filter((p): p is KvPair => p !== null)
}

function safeJsonStringify(value: unknown): string {
  if (value === undefined || value === null) return ''
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

// ─── Code block ────────────────────────────────────────────────────────────
const CODE_LANGUAGES = [
  '',
  'typescript',
  'javascript',
  'python',
  'sql',
  'json',
  'yaml',
  'bash',
  'go',
  'rust',
  'java',
  'csharp',
  'html',
  'css',
] as const

function CodeBlockEditor({
  value,
  onChange,
  disabled,
}: {
  value: unknown
  onChange: (value: unknown) => void
  disabled: boolean
}) {
  const { t } = useTranslation('templates')
  let code = ''
  let language = ''
  if (typeof value === 'string') {
    code = value
  } else if (value && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>
    if (typeof obj['code'] === 'string') code = obj['code']
    if (typeof obj['language'] === 'string') language = obj['language']
  }

  function patch(next: { code?: string; language?: string }) {
    onChange({ code: next.code ?? code, language: next.language ?? language })
  }

  return (
    <div className="space-y-2">
      <label className="block">
        <span className="text-xs font-medium text-ink-muted">
          {t('blocks.code_block.language_label')}
        </span>
        <select
          value={language}
          onChange={(e) => patch({ language: e.target.value })}
          disabled={disabled}
          className="mt-1 bg-canvas border border-line rounded-md px-3 py-1.5 text-xs font-mono"
        >
          {CODE_LANGUAGES.map((lang) => (
            <option key={lang} value={lang}>
              {lang || t('blocks.code_block.no_language')}
            </option>
          ))}
        </select>
      </label>
      <textarea
        value={code}
        onChange={(e) => patch({ code: e.target.value })}
        rows={10}
        spellCheck={false}
        disabled={disabled}
        placeholder={t('blocks.code_block.code_placeholder')}
        className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:border-brand resize-y disabled:opacity-50 whitespace-pre"
      />
    </div>
  )
}

// ─── Fixed heading (no editable, contenedor estructural) ──────────────────
function FixedHeadingEditor() {
  const { t } = useTranslation('templates')
  return (
    <div className="rounded-md border border-line bg-surface-muted/40 p-3">
      <p className="font-body text-xs text-ink-muted">
        {t('blocks.fixed_heading.editor_hint')}
      </p>
    </div>
  )
}

// ─── Fixed block (no editable en runtime — texto fijo del template) ───────
/** El cuerpo del fixed_block vive en `i18n.{lang}.sections[].body` y se
 * edita desde el Designer (no desde el draft del artifact). En el draft
 * solo mostramos un hint explicando al usuario que es contenido fijo. */
function FixedBlockEditor() {
  const { t } = useTranslation('templates')
  return (
    <div className="rounded-md border border-line bg-surface-muted/40 p-3">
      <p className="font-body text-xs text-ink-muted">
        {t('blocks.fixed_block.editor_hint')}
      </p>
    </div>
  )
}


// ─── Structured table editor (typed columns, schema-driven) ───────────────
interface StructuredTableEditorColumn {
  key: string
  type: string
  required?: boolean
  options?: string[]
  prefix?: string
  pad?: number
  /** Doc 24 v2 — label localizado del header. Cuando viene, el editor
   * lo usa en vez de `column.key.toUpperCase()`. */
  label?: string
  /** Doc 24 v2 — map `{value: label}` para mostrar el label localizado
   * en el dropdown del enum. El value persistido NO cambia. */
  optionLabels?: Record<string, string>
  /** Doc 19 §10 — solo `type === 'currency'`. ISO 4217 que pre-selecciona
   * el dropdown al crear nueva fila. */
  defaultCurrency?: string
  /** Doc 19 §10 — solo `type === 'currency'`. Restringe el dropdown
   * del editor a estos códigos ISO 4217. Si está ausente, dropdown
   * muestra el set por default `DEFAULT_CURRENCY_OPTIONS`. */
  allowedCurrencies?: string[]
}

/** Doc 19 §10 — lista por defecto de monedas que el dropdown muestra
 * cuando la columna no declara `allowedCurrencies`. Son las del MVP
 * (LatAm + US + EUR + GBP). Más se agregan por demanda del tenant. */
const DEFAULT_CURRENCY_OPTIONS = [
  'MXN',
  'USD',
  'EUR',
  'COP',
  'BRL',
  'CLP',
  'ARS',
  'GBP',
] as const

interface StructuredTableEditorMeta {
  columns?: StructuredTableEditorColumn[]
  min_rows?: number
  max_rows?: number
  [extra: string]: unknown
}

function StructuredTableEditor({
  value,
  meta,
  onChange,
  disabled,
  locale = 'es',
}: {
  value: unknown
  meta: TemplateSectionMeta
  onChange: (value: unknown) => void
  disabled: boolean
  locale?: string
}) {
  const { t } = useTranslation('templates')
  const m = meta as StructuredTableEditorMeta
  // Doc 24 §3 — adaptador v1↔v2: detecta `table_config` shape v2 y mapea
  // a `columns` lite (con `options` derivados de `enum_values.value`).
  // Sin esto, templates v2 muestran "no_columns_warning" porque
  // `meta.columns` ya no existe — vive en `meta.table_config.columns`.
  const normalized = normalizeTableSchema(
    m as unknown as Parameters<typeof normalizeTableSchema>[0],
    locale,
  )
  const columns = normalized.columns
  const maxRows =
    typeof m.max_rows === 'number' ? m.max_rows : Number.POSITIVE_INFINITY

  // Parse defensivo del value: { rows: [...] } o array directo legacy.
  const rows: Array<Record<string, unknown>> = (() => {
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Array.isArray((value as Record<string, unknown>)['rows'])
    ) {
      return (value as Record<string, unknown>)['rows'] as Array<
        Record<string, unknown>
      >
    }
    if (Array.isArray(value)) return value as Array<Record<string, unknown>>
    return []
  })()

  if (columns.length === 0) {
    return (
      <p className="font-body text-sm text-ink-subtle italic px-2 py-3">
        {t('blocks.structured_table.no_columns_warning')}
      </p>
    )
  }

  function commitRows(next: Array<Record<string, unknown>>) {
    onChange({ rows: next })
  }

  function addRow() {
    if (rows.length >= maxRows) return
    commitRows([...rows, {}])
  }
  function removeRow(idx: number) {
    commitRows(rows.filter((_, i) => i !== idx))
  }
  function updateCell(idx: number, colKey: string, raw: unknown) {
    const next = rows.map((r, i) =>
      i === idx ? { ...r, [colKey]: raw } : r,
    )
    commitRows(next)
  }

  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <p className="font-body text-sm text-ink-subtle italic">
          {t('blocks.structured_table.no_rows_hint')}
        </p>
      ) : (
        rows.map((row, rIdx) => (
          <article
            key={rIdx}
            className="rounded-md border border-line bg-surface-muted/30 p-3"
          >
            <header className="mb-2 flex items-center justify-between">
              <span className="font-mono text-[10px] text-ink-subtle">
                #{rIdx + 1}
              </span>
              <button
                type="button"
                onClick={() => removeRow(rIdx)}
                disabled={disabled}
                aria-label={t('blocks.structured_table.remove_row')}
                className="p-1 rounded text-ink-subtle hover:text-critical hover:bg-critical-soft disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </header>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {columns.map((col) => (
                <StructuredCellEditor
                  key={col.key}
                  column={col}
                  rowIdx={rIdx}
                  value={row[col.key]}
                  disabled={disabled}
                  onChange={(v) => updateCell(rIdx, col.key, v)}
                />
              ))}
            </div>
          </article>
        ))
      )}
      {!disabled && rows.length < maxRows ? (
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1.5 text-xs text-brand hover:underline"
        >
          <Plus className="w-3.5 h-3.5" />
          {t('blocks.structured_table.add_row')}
        </button>
      ) : null}
    </div>
  )
}

/** Editor de una celda según el type declarado de la columna. */
function StructuredCellEditor({
  column,
  rowIdx,
  value,
  disabled,
  onChange,
}: {
  column: StructuredTableEditorColumn
  rowIdx: number
  value: unknown
  disabled: boolean
  onChange: (v: unknown) => void
}) {
  const { t } = useTranslation('projects')
  const labelClass =
    'text-[10px] uppercase tracking-wider font-semibold text-ink-muted'
  const inputClass =
    'mt-0.5 w-full bg-canvas border border-line rounded-md px-2 py-1 text-sm focus:outline-none focus:border-brand disabled:opacity-50'

  if (column.type === 'autonum') {
    const n = rowIdx + 1
    const padded =
      column.pad && column.pad > 0
        ? String(n).padStart(column.pad, '0')
        : String(n)
    const display = `${column.prefix ?? ''}${padded}`
    return (
      <label className="block">
        <span className={labelClass}>{column.label ?? column.key}</span>
        <input
          type="text"
          value={display}
          disabled
          className="mt-0.5 w-full bg-surface-muted border border-line rounded-md px-2 py-1 text-sm font-mono text-ink-subtle"
        />
      </label>
    )
  }

  if (column.type === 'text') {
    return (
      <label className="block">
        <span className={labelClass}>{column.label ?? column.key}</span>
        <input
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={inputClass}
        />
      </label>
    )
  }

  if (column.type === 'number') {
    return (
      <label className="block">
        <span className={labelClass}>{column.label ?? column.key}</span>
        <input
          type="number"
          value={
            typeof value === 'number'
              ? value
              : typeof value === 'string'
                ? value
                : ''
          }
          onChange={(e) => {
            const raw = e.target.value
            const n = raw.trim() ? Number(raw) : null
            onChange(n !== null && Number.isFinite(n) ? n : null)
          }}
          disabled={disabled}
          className={`${inputClass} font-mono`}
        />
      </label>
    )
  }

  if (column.type === 'currency') {
    // Doc 19 §10 — celda canónica `{amount, code}`. El input numérico
    // edita el amount; el dropdown inline edita el code. Si el value
    // legacy es scalar number (data pre-v2.1.0), lo tratamos como amount
    // y aplicamos defaultCurrency hasta que el user lo guarde.
    const cell =
      value && typeof value === 'object' && !Array.isArray(value)
        ? (value as { amount?: unknown; code?: unknown })
        : null
    const currentAmount =
      cell && typeof cell.amount === 'number' && Number.isFinite(cell.amount)
        ? cell.amount
        : cell && typeof cell.amount === 'string'
          ? cell.amount
          : typeof value === 'number'
            ? value
            : ''
    const currentCode =
      cell && typeof cell.code === 'string' && /^[A-Z]{3}$/.test(cell.code)
        ? cell.code
        : column.defaultCurrency ?? 'MXN'
    // Lista de monedas del dropdown: explícita de la columna o default
    // global. Aseguramos que el code actual aparezca aunque no esté en
    // la lista (caso legacy / cliente custom) — al inicio para no perder.
    const baseList =
      column.allowedCurrencies && column.allowedCurrencies.length > 0
        ? column.allowedCurrencies
        : ([...DEFAULT_CURRENCY_OPTIONS] as string[])
    const currencyOptions = baseList.includes(currentCode)
      ? baseList
      : [currentCode, ...baseList]

    function commit(nextAmount: number | null, nextCode: string): void {
      // Persistimos siempre el objeto `{amount, code}` aunque amount
      // sea null — así la moneda elegida sobrevive entre cambios sin
      // amount aún. El renderer trata `amount: null` como celda vacía
      // (devuelve ""), y la validación required-row la detectará al
      // submit. Si el usuario limpia tanto amount como code, mandamos
      // null para que la celda quede limpia por completo.
      if (nextAmount === null && !nextCode) {
        onChange(null)
        return
      }
      onChange({ amount: nextAmount, code: nextCode })
    }

    return (
      <label className="block">
        <span className={labelClass}>{column.label ?? column.key}</span>
        <div className="mt-0.5 flex gap-1">
          <input
            type="number"
            value={currentAmount}
            onChange={(e) => {
              const raw = e.target.value
              const n = raw.trim() ? Number(raw) : null
              commit(
                n !== null && Number.isFinite(n) ? n : null,
                currentCode,
              )
            }}
            disabled={disabled}
            className={`${inputClass} font-mono flex-1 mt-0`}
          />
          <select
            value={currentCode}
            onChange={(e) => {
              const nextCode = e.target.value
              const amt =
                typeof currentAmount === 'number' ? currentAmount : null
              commit(amt, nextCode)
            }}
            disabled={disabled}
            className="bg-canvas border border-line rounded-md px-2 py-1 text-xs font-mono focus:outline-none focus:border-brand disabled:opacity-50"
            aria-label={`${column.label ?? column.key} — moneda`}
          >
            {currencyOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </label>
    )
  }

  if (column.type === 'date') {
    return (
      <label className="block">
        <span className={labelClass}>{column.label ?? column.key}</span>
        <input
          type="date"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={`${inputClass} font-mono`}
        />
      </label>
    )
  }

  if (column.type === 'enum' || column.type === 'select') {
    const opts = column.options ?? []
    const optionLabels = column.optionLabels ?? {}
    const current = typeof value === 'string' ? value : ''
    // Un valor guardado que no es una de las opciones (OT-0042) se muestra
    // tal cual y marcado: si el select cayera a "—", el usuario no sabría
    // que hay algo que corregir y el guardado lo rechazaría.
    const invalid = current !== '' && !opts.includes(current)
    return (
      <label className="block">
        <span className={labelClass}>{column.label ?? column.key}</span>
        <select
          value={current}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          aria-invalid={invalid}
          className={`${inputClass} ${invalid ? 'border-critical text-critical' : ''}`}
        >
          <option value="">—</option>
          {invalid && (
            <option value={current} disabled>
              {t('official_editor.structured.invalid_option', { value: current })}
            </option>
          )}
          {opts.map((opt) => (
            <option key={opt} value={opt}>
              {optionLabels[opt] ?? opt}
            </option>
          ))}
        </select>
      </label>
    )
  }

  // Tipo desconocido — fallback a text.
  return (
    <label className="block">
      <span className={labelClass}>{column.key}</span>
      <input
        type="text"
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={inputClass}
      />
    </label>
  )
}

// ─── Diagram (Mermaid) ─────────────────────────────────────────────────────
function DiagramEditor({
  value,
  onChange,
  disabled,
  placeholder,
}: {
  value: unknown
  onChange: (value: unknown) => void
  disabled: boolean
  /** Placeholder textarea — específico del subtipo Mermaid para que el
   * usuario vea la sintaxis idiomática esperada (flowchart, sequenceDiagram,
   * stateDiagram-v2, etc.) cuando el campo está vacío. */
  placeholder?: string
}) {
  const { t } = useTranslation('templates')
  const source = parseDiagramSource(value)
  const [tab, setTab] = useState<'source' | 'preview'>('source')
  const [debouncedSource, setDebouncedSource] = useState<string>(source)

  // Debounce 500ms en el preview para no re-renderizar mermaid en cada tecla.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSource(source), 500)
    return () => clearTimeout(handle)
  }, [source])

  function commitSource(next: string) {
    // Persistimos como string crudo — el shape es consistente con code_block.
    onChange(next)
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setTab('source')}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
            tab === 'source'
              ? 'bg-brand-soft text-brand border border-brand/30'
              : 'text-ink-muted hover:text-ink hover:bg-surface-muted'
          }`}
        >
          <Code2 className="w-3.5 h-3.5" />
          {t('blocks.diagram.tab_source')}
        </button>
        <button
          type="button"
          onClick={() => setTab('preview')}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
            tab === 'preview'
              ? 'bg-brand-soft text-brand border border-brand/30'
              : 'text-ink-muted hover:text-ink hover:bg-surface-muted'
          }`}
        >
          <Eye className="w-3.5 h-3.5" />
          {t('blocks.diagram.tab_preview')}
        </button>
        <a
          href="https://mermaid.js.org/intro/syntax-reference.html"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto text-[11px] text-ink-subtle hover:text-brand hover:underline"
        >
          {t('blocks.diagram.docs_link')}
        </a>
      </div>

      {tab === 'source' ? (
        <textarea
          value={source}
          onChange={(e) => commitSource(e.target.value)}
          rows={12}
          spellCheck={false}
          disabled={disabled}
          placeholder={placeholder ?? t('blocks.diagram.source_placeholder')}
          className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:border-brand resize-y disabled:opacity-50 whitespace-pre"
        />
      ) : debouncedSource.trim() === '' ? (
        <p className="font-body text-xs text-ink-subtle italic">
          {t('blocks.diagram.preview_empty')}
        </p>
      ) : (
        <MermaidRender source={debouncedSource} />
      )}
    </div>
  )
}
